import fetch from 'isomorphic-fetch'
import moment from 'moment'
import { find, map, isUndefined } from 'lodash'

import { getHeaders } from '../lib/http'
import { getPrintableAwsUploadSigninUrl } from '../apis'
import { store } from '../index'
import { postBlob } from '../lib/http'

export class RobustPhotographUploader {

  constructor(files, galleryPhotographs, galleryId, countryCode, on_file_upload_progress) {
    this.files = files
    this.galleryPhotographs = galleryPhotographs
    this.galleryId = galleryId
    this.countryCode = countryCode
    this.on_file_upload_progress = on_file_upload_progress
    this.successes = []
    this.failures = []
    this.reason = null
    this.BATCH_SIZE = 8
    this.RETRIES = 5
    this.DELAY_MS = 1000
  }

  async uploadFiles() {

    map(this.files, (file) => {
      this.on_file_upload_progress(file, 'pending')
    })

    return await Promise.resolve(this.batchExecute(this.files)).then(() => {
      return {
        successes: this.successes,
        failures: this.failures,
        reason: this.reason,
      }
    })
  }

  async execute(file) {
    try {
      const duplicatePhotograph = find(this.galleryPhotographs,
                                    (photograph) => file.name === photograph.photo_name
      )
      const isDuplicate = !isUndefined(duplicatePhotograph)
      if (isDuplicate) {
        this.on_file_upload_progress(file, 'success')
        this.successes.push({
          status: 'success',
          file: file.name,
          photographId: duplicatePhotograph?.id,
        })
        return

      }
      
      const s3SignInData = await this.getS3SignInData(file)

      const values = await this.uploadToS3(file, s3SignInData)

      const response = await this.savePhotographToGallery(values)

      this.on_file_upload_progress(file, 'success')
      this.successes.push({
        status: 'success',
        file: file.name,
        photographId: values?.id,
      })
      return

    } catch (error) {
      this.on_file_upload_progress(file, 'failure', error?.message)

      this.failures.push({
        status: 'failure',
        file: file.name,
        error: error?.message
      })
      this.reason = error?.message
      return

    }
  }

  async getS3SignInData(file) {
    function callback(response) {
      try {
        const url = response.data.pre_signin_res.url
        const data = new window.FormData()

        Object.keys(response.data.pre_signin_res.fields).forEach((key) => {
          data.append(key, response.data.pre_signin_res.fields[key])
        })
        data.append("file", file)
        
        return {
          url: url,
          data: data,
          s3FilePath: response.data.s3_filepath,
          awsFileUrl: response.data.aws_file_url
        }
      } catch (error) {
        throw new Error(
          `reason=aws_signin_url_error, name=${error?.name}, msg: ${error?.message}`
        )
      }
    }

    const params = [this.countryCode, file.name, this.galleryId]

    const func = getPrintableAwsUploadSigninUrl

    return await this.retryWrapper(func, params, callback)
  }

  async uploadToS3(file, s3SignInData) {
    const {
      url, data, s3FilePath, awsFileUrl
    } = s3SignInData
    const that = this

    function callback(response) {
      try {
        if (response.ok) {
          const values = new window.FormData()
          values.append('aws_path', s3FilePath)
          values.append('filename', file.name)
          values.append('gallery', that.galleryId)
          values.append('content_type', file.type)
          values.append('country_code', that.countryCode)
          values.append('photograph_taken_at', moment(file.lastModifiedDate).format())
          values.append('filesize', file.size)

          return values
        } else {
          throw new Error('Bad response from server')
        }
      } catch (error) {
        throw new Error(
          `reason=aws_upload_error, name=${error?.name}, msg: ${error?.message}`
        )
      }
    }

    const params = [url, {
      method: "POST",
      headers: getHeaders(null, null, { include_content_type: false }),
      body: data,
    }]

    const func = fetch

    return await this.retryWrapper(func, params, callback)
  }

  async savePhotographToGallery(values) {
    function callback(result) {
      try {
        const [ json, response, isSuccessful ] = result
        if (isSuccessful) {
          return json
        } else {
          throw new Error('Bad response from server')
        }
      } catch (error) {
        throw new Error(
          `reason=photograph_api_error, name=${error?.name}, msg: ${error?.message}`
        )
      }
    }

    const params = ['photographer/photograph', values]

    const func = postBlob

    return await this.retryWrapper(func, params, callback)
  }

  async batchExecute(list) {
    const results = []

    for (let batchStart = 0; batchStart < list.length; batchStart += this.BATCH_SIZE) {
      const batchPromises = []
      for (let i = 0; i < this.BATCH_SIZE && batchStart + i < list.length; i++) {
        batchPromises.push(this.execute(list[batchStart + i]))
      }

      const batchResults = await Promise.all(batchPromises)
      results.push(batchResults)
    }

    return results
  }

  async retryWrapper(func, params, callback) {
    let attempt = 0;
    
    while (attempt < this.RETRIES) {
      try {
        attempt++

        return await func(...params).then((res) => callback(res))
      } catch (error) {
        if (attempt >= this.RETRIES) {
          throw new Error(
            `reason=retries_exceeded_error, name=${error?.name}, msg: ${error?.message}`
          )
        }
        await new Promise(resolve => setTimeout(resolve, this.DELAY_MS * Math.pow(2, attempt - 1)));
      }
    }
  }
  
}
